/**
 * Which fields of the app's search body the **public AI Ark API** does not
 * accept, and how to remove them.
 *
 * The app's API Payload tab is built from the same `transform()` the in-app
 * search uses, so it produces the *app backend's* request shape. The public API
 * is a different service with a ~90% overlapping — but not identical — payload
 * contract: it has no binding for the workspace/CRM-side fields the app adds.
 * Showing that body as "copy it and call the API" is wrong for any search that
 * uses one of them.
 *
 * The full field-by-field comparison lives in
 * `docs/falcon-vs-elephant-filters.md` (supplied by backend, `CU-86e2ybqd5`).
 * **That file is the contract; this table is only the subset the frontend
 * actually emits.** Nothing here detects drift — when the public API's payload
 * changes, this table has to be revisited by hand.
 *
 * See `docs/copy-apply-search-payload.md` §6.2.
 */

/** Path segment wildcard — matches exactly one segment (used for array items). */
const WILDCARD = '*';

export type FalconGap = {
  /** Dot-path into the API body. `*` matches one segment (an array index). */
  pattern: string;
  /** How the user knows this filter in the app. */
  label: string;
  /** Minimal hover explanation. Shown verbatim in the payload tooltip. */
  note: string;
};

/** Keeps every tooltip phrased the same way regardless of the filter's name. */
const removedOnCopy = (subject: string) =>
  `${subject} has no equivalent in the AI Ark API — this line is removed from the copied payload.`;

/**
 * Ordered so the warning alert reads predictably: envelope first, then people
 * filters, then company filters, then the metric special case.
 *
 * Three entries (`contact.headline`, `contact.experienceChange`,
 * `account.exists`) are app-backend fields the frontend never emits. They are
 * kept so this table mirrors the contract rather than today's emission set —
 * they simply never match.
 */
export const FALCON_GAPS: readonly FalconGap[] = [
  {
    pattern: 'type',
    label: 'Search type (internal)',
    note: removedOnCopy('The app-internal search type'),
  },
  {
    pattern: 'modality',
    label: 'Saved / Total tab',
    note: removedOnCopy('The Saved / Total dataset tab'),
  },

  // ── people (contact.*) ────────────────────────────────────────────────────
  {
    pattern: 'contact.geoLocation',
    label: 'Zip Code radius (people)',
    note: removedOnCopy('The people Zip Code radius filter'),
  },
  {
    pattern: 'contact.jobChange',
    label: 'Job Change',
    note: removedOnCopy('The Job Change filter'),
  },
  {
    pattern: 'contact.experienceChange',
    label: 'Experience Change',
    note: removedOnCopy('The Experience Change filter'),
  },
  {
    pattern: 'contact.headline',
    label: 'Headline',
    note: removedOnCopy('The Headline filter'),
  },
  {
    pattern: 'contact.maxPeoplePerCompany',
    label: 'Max People Per Company',
    note: removedOnCopy('The Max People Per Company limit'),
  },
  {
    pattern: 'contact.label',
    label: 'Labels (people)',
    note: removedOnCopy('The Labels filter'),
  },
  {
    pattern: 'contact.user',
    label: 'Record Ownership (people)',
    note: removedOnCopy('The Record Ownership filter'),
  },
  {
    pattern: 'contact.export',
    label: 'Export Request (people)',
    note: removedOnCopy('The Export Request filter'),
  },
  {
    pattern: 'contact.enrichment',
    label: 'Enrichment Request (people)',
    note: removedOnCopy('The Enrichment Request filter'),
  },
  {
    pattern: 'contact.searchList',
    label: 'Lists (people)',
    note: removedOnCopy('The Lists filter'),
  },

  // ── companies (account.*) ─────────────────────────────────────────────────
  {
    pattern: 'account.relevancy',
    label: 'Relevancy',
    note: removedOnCopy('The Relevancy filter'),
  },
  {
    pattern: 'account.exists',
    label: 'Has records',
    note: removedOnCopy('This app-only flag'),
  },
  {
    pattern: 'account.label',
    label: 'Labels (companies)',
    note: removedOnCopy('The Labels filter'),
  },
  {
    pattern: 'account.user',
    label: 'Record Ownership (companies)',
    note: removedOnCopy('The Record Ownership filter'),
  },
  {
    pattern: 'account.export',
    label: 'Export Request (companies)',
    note: removedOnCopy('The Export Request filter'),
  },
  {
    pattern: 'account.enrichment',
    label: 'Enrichment Request (companies)',
    note: removedOnCopy('The Enrichment Request filter'),
  },
  {
    pattern: 'account.searchList',
    label: 'Lists (companies)',
    note: removedOnCopy('The Lists filter'),
  },

  // The only *partial* gap: the API supports `metric`, but not the per-row
  // include/exclude flag. Dropping it changes what the row *means*, so this note
  // states the consequence instead of reusing `removedOnCopy`. See doc §6.2.2.
  {
    pattern: `account.metric.employee.${WILDCARD}.option`,
    label: 'Employees by Department — include/exclude mode',
    note:
      "The API has no include/exclude mode for this row. It's removed on copy, " +
      'so the API treats the row as a plain include.',
  },
  {
    pattern: `account.metric.growth.${WILDCARD}.option`,
    label: 'Headcount Growth — include/exclude mode',
    note:
      "The API has no include/exclude mode for this row. It's removed on copy, " +
      'so the API treats the row as a plain include.',
  },
];

/** A gap that is actually present in a given body, with its concrete paths. */
export type FalconGapHit = {
  gap: FalconGap;
  /** Concrete paths (wildcards resolved), e.g. `account.metric.growth.0.option`. */
  paths: string[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function join(parent: string, key: string): string {
  return parent ? `${parent}.${key}` : key;
}

/** `*` matches exactly one segment, so array items resolve but depth can't drift. */
function matches(path: string, pattern: string): boolean {
  const actual = path.split('.');
  const expected = pattern.split('.');
  if (actual.length !== expected.length) return false;
  return expected.every((seg, i) => seg === WILDCARD || seg === actual[i]);
}

function findGap(path: string): FalconGap | undefined {
  return FALCON_GAPS.find((gap) => matches(path, gap.pattern));
}

/** Every path in `body`, including intermediate containers. */
function walk(value: unknown, path: string, out: string[]): void {
  if (path) out.push(path);
  if (Array.isArray(value)) {
    value.forEach((item, i) => walk(item, join(path, String(i)), out));
  } else if (isRecord(value)) {
    for (const [key, child] of Object.entries(value)) {
      walk(child, join(path, key), out);
    }
  }
}

/**
 * The app-only fields present in `body`, in `FALCON_GAPS` order (so the warning
 * alert doesn't reshuffle as the user edits filters).
 */
export function findUnsupported(body: unknown): FalconGapHit[] {
  const paths: string[] = [];
  walk(body, '', paths);

  const hits: FalconGapHit[] = [];
  for (const gap of FALCON_GAPS) {
    const found = paths.filter((path) => matches(path, gap.pattern));
    if (found.length) hits.push({ gap, paths: found });
  }
  return hits;
}

/** Marker for "this key was removed", so callers can prune emptied containers. */
const DROP = Symbol('drop');

function stripValue(value: unknown, path: string): unknown | typeof DROP {
  if (Array.isArray(value)) {
    const next = value
      .map((item, i) => stripValue(item, join(path, String(i))))
      .filter((item) => item !== DROP);
    return next;
  }

  if (isRecord(value)) {
    const next: Record<string, unknown> = {};
    let removedSomething = false;

    for (const [key, child] of Object.entries(value)) {
      const childPath = join(path, key);

      if (findGap(childPath)) {
        removedSomething = true;
        continue;
      }

      const stripped = stripValue(child, childPath);
      if (stripped === DROP) {
        removedSomething = true;
        continue;
      }
      next[key] = stripped;
    }

    // A container left empty *by our own removal* is noise in the copied
    // payload. One that arrived empty is left alone — the app always sends
    // `contact: {}` / `account: {}`, and hiding that would misrepresent the
    // request the app actually makes.
    if (removedSomething && Object.keys(next).length === 0) return DROP;
    return next;
  }

  return value;
}

/**
 * `body` with every app-only field removed — what **Copy Payload** puts on the
 * clipboard. The *displayed* body keeps them, so the user still sees what the
 * app really sends; the highlight is what marks the difference.
 */
export function stripForFalcon<T>(body: T): T {
  const stripped = stripValue(body, '');
  return (stripped === DROP ? {} : stripped) as T;
}
